import colors from 'lib/constants/resourceColors';
import contractTypeRemarkId from 'lib/helpers/contractTypeRemarkId';
import Privileges from 'lib/constants/privileges';

// Administration > Contract Types. Modeled on resources/taskTypes.js (simplest admin
// lookup page) but keyed on `type` instead of `id` because contract_types has no id
// column (PK is the `type` VARCHAR). Selecting a type on a contract snapshots these
// default_* values (decisions #46-#49), so this page is where those defaults are
// authored/edited. `type` is createOnly since it is the FK target for contracts.
const form = [
    {key: 'type', label: 'Type (Unique ID)', createOnly: true},
    {key: 'description', type: 'longtext'},
    {key: 'default_amount', label: 'Default Amount', type: 'number'},
    {key: 'default_term_months', label: 'Default Term (months)', type: 'number'},
    {key: 'default_renewal_term_months', label: 'Default Renewal Term (months)', type: 'number'},
    {key: 'default_increase_pct', label: 'Default Auto-Renew Increase %', type: 'number'},
    {key: 'default_labor_discount_pct', label: 'Default Labor Discount %', type: 'number'},
    // Scalar fallback rates. Used for any active labor rate type not given an explicit
    // Default Rate below: on contract creation each such rate type is seeded from these,
    // and they cover rate types added after the type was configured. A type can be defined
    // with only these (no Default Rates rows) and still produce a full set of contract rates.
    {key: 'default_regular_rate', label: 'Fallback Regular Rate', type: 'currency'},
    {key: 'default_ot_rate', label: 'Fallback OT Rate', type: 'currency'},
    // T&M (Time & Material) fallback pair -- the out-of-contract-scope rate, parallel to the
    // covered Fallback Regular/OT above. Seeds tm_regular_rate / tm_ot_rate onto Default Rates
    // and, at contract creation, onto contract_rates.
    {key: 'default_tm_regular_rate', label: 'Fallback T&M Regular Rate', type: 'currency'},
    {key: 'default_tm_ot_rate', label: 'Fallback T&M OT Rate', type: 'currency'},

    // Per-rate-type default amounts (labor + non-labor). These are the rows createContract
    // snapshots onto a new contract's rates (procedures/contracts.js seedContractRates). Shown
    // on the saved contract-type page (references sub-editors are suppressed on create); author
    // a type's defaults here after creating it. Keyed by rate_type_code -> rate_types; the rate
    // type is read-only once added (delete + re-add to change it).
    {key: 'default_rates',
        type: 'references',
        heading: 'Default Rates',
        createLabel: 'Add Default Rate',
        createPrivileges: [Privileges.CREATE_CONTRACT_TYPE_RATES],
        readPrivileges: [Privileges.READ_CONTRACT_TYPE_RATES],
        deletePrivileges: [Privileges.DELETE_CONTRACT_TYPE_RATES],
        dataUrl: '/contract_type_rates?contract_type=:type',
        createUrl: () => '/contract_type_rates',
        deleteUrl: () => '/contract_type_rates/:id',
        editUrl: '/contract_type_rates/:id',
        editPrivileges: [Privileges.UPDATE_CONTRACT_TYPE_RATES],
        editFields: [
            {key: 'rate_type_name', label: 'Rate Type', static: true, readOnly: true},
            {key: 'regular_rate', label: 'Regular Rate', type: 'currency'},
            {key: 'ot_rate', label: 'OT Rate', type: 'currency'},
            {key: 'tm_regular_rate', label: 'T&M Regular Rate', type: 'currency'},
            {key: 'tm_ot_rate', label: 'T&M OT Rate', type: 'currency'},
        ],
        injectedValues: (values) => { return {contract_type: values.type} },
        formFields: [
            {key: 'rate_type_code',
                label: 'Rate Type',
                type: 'options',
                required: true,
                optionsURL: '/enums/rate_types',
                optionsMapper: (rt) => ({value: rt.code, label: rt.name ? `${rt.name} (${rt.code})` : rt.code})},
            {key: 'regular_rate', label: 'Regular Rate', type: 'currency'},
            {key: 'ot_rate', label: 'OT Rate', type: 'currency'},
            {key: 'tm_regular_rate', label: 'T&M Regular Rate', type: 'currency'},
            {key: 'tm_ot_rate', label: 'T&M OT Rate', type: 'currency'},
        ],
        tableProps: {
            columns: [
                {heading: 'Rate Type', accessor: 'rate_type_name'},
                {heading: 'Category', accessor: 'rate_type_category'},
                {heading: 'Regular Rate', accessor: 'regular_rate', type: 'currency'},
                {heading: 'OT Rate', accessor: 'ot_rate', type: 'currency'},
                {heading: 'T&M Regular Rate', accessor: 'tm_regular_rate', type: 'currency'},
                {heading: 'T&M OT Rate', accessor: 'tm_ot_rate', type: 'currency'},
            ]
        }
    },

    // Per-vendor DEFAULT material discounts. createContract snapshots these rows onto each
    // new contract's contract_material_discounts (procedures/contracts.js seedMaterialDiscounts),
    // so this is where a type's material-discount defaults are authored -- this block was
    // removed earlier by mistake and restored because material discounts drive new-contract
    // defaults. Suppressed on create (references sub-editors only render on the saved type);
    // add a type's defaults here after creating it. Mirrors the contracts.js material_discounts
    // editor: vendor is the row identity (contract_type + vendor_id), so it's picked via
    // autocomplete on add and read-only on edit -- to change it, delete + re-add. Only
    // discount_pct is a privileged server-side write.
    {key: 'material_discounts',
        type: 'references',
        heading: 'Default Material Discounts',
        createLabel: 'Add Material Discount',
        createPrivileges: [Privileges.CREATE_CONTRACT_TYPE_MATERIAL_DISCOUNTS],
        readPrivileges: [Privileges.READ_CONTRACT_TYPE_MATERIAL_DISCOUNTS],
        deletePrivileges: [Privileges.DELETE_CONTRACT_TYPE_MATERIAL_DISCOUNTS],
        dataUrl: '/contract_type_material_discounts?contract_type=:type',
        createUrl: () => '/contract_type_material_discounts',
        deleteUrl: () => '/contract_type_material_discounts/:id',
        editUrl: '/contract_type_material_discounts/:id',
        editPrivileges: [Privileges.UPDATE_CONTRACT_TYPE_MATERIAL_DISCOUNTS],
        editFields: [
            {key: 'vendor_name', label: 'Vendor', static: true, readOnly: true},
            {key: 'discount_pct', label: 'Discount %', type: 'percentage'},
        ],
        injectedValues: (values) => { return {contract_type: values.type} },
        formFields: [
            {key: 'vendor_id',
                displayKey: 'vendor_name',
                label: 'Vendor',
                type: 'autocomplete',
                suggestFormat: (v) => { return {value: v.id, text: v.name} },
                suggestUrl: '/vendors',
                suggestFields: ['id', 'name']},
            {key: 'discount_pct', label: 'Discount %', type: 'percentage'},
        ],
        tableProps: {
            columns: [
                {heading: 'Vendor', accessor: 'vendor_name'},
                {heading: 'Discount %', accessor: 'discount_pct', type: 'percentage'},
            ]
        }
    },
];

export default {
    defaults: {
        name: 'Contract Type',
        color: colors.contractTypes,
        identifierParam: 'type',
        formFields: form,
    },

    view: {
        title: (t) => `${t.type} Contract Type`,
        // contract_types has no uuid id (PK is the text `type`), but remarks.object_id
        // is a uuid column. Derive a stable v5 uuid from `type` (mirrored on the back
        // end) so History fetches/posts with a valid uuid instead of the raw string,
        // which previously 500'd the GET /remarks call ('Failed to fetch remarks').
        remarks: {
            object_id_key: (t) => contractTypeRemarkId(t.type),
            object_type: 'contract type',
            header: (t) => `${t.type} Contract Type`,
        },
    },

    search: {
        fields: ['type', 'description', 'default_amount', 'default_term_months', 'default_increase_pct', 'updated_at'],
        links: ['new'],
        searchBy: ['type', 'description'],
        tableProps: {
            rowURL: '/contract_types/:type',
            columns: [
                {heading: 'Type', accessor: 'type'},
                {heading: 'Description', accessor: 'description'},
                {heading: 'Default Amount', accessor: 'default_amount'},
                {heading: 'Term (mo)', accessor: 'default_term_months'},
                {heading: 'Increase %', accessor: 'default_increase_pct'},
            ],
            showHeading: false,
        }
    },
};
